// src/components/Dashboard.js

import React, { useEffect, useRef, useState } from "react";
import { getDashboardSummary } from "../api/dashboard";
import { getProviderFundamentals, getProviderNews, getProviderPrice } from "../api/providers";
import { getMarketSession } from "../api/market";
import { runRuntimeMaintenance } from "../api/portfolio";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import ResponsiveContainer from "./ResponsiveContainer";

function fmt(value, digits = 2) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "--";
  }
  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function toNumberOrNull(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function KPICard({ label, value, sub, highlight, chip }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 130,
        flex: 1,
        borderLeft: highlight ? `4px solid ${highlight}` : undefined,
        bgcolor: highlight ? alpha(highlight, 0.06) : undefined,
        transition: "background-color 0.2s",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" display="block">
          {sub}
        </Typography>
      )}
      {chip}
    </Paper>
  );
}

function ProviderHealthRow({ name, entry }) {
  const status = entry?.status ?? "unknown";
  const colorMap = { ok: "success", warn: "warning", error: "error" };
  const circuitOpen = String(entry?.error_type || "").toLowerCase() === "circuit_open"
    || String(entry?.message || "").toLowerCase().includes("circuit open");
  const mismatchDirection = String(entry?.budget_mismatch_direction || "").toLowerCase();
  const planLabel = entry?.plan ?? "--";
  const usedCalls = Number(entry?.calls_used_today);
  const dailyBudget = Number(entry?.daily_budget);
  const configuredDailyBudget = Number(entry?.configured_daily_budget);
  const effectiveDailyBudget = Number(entry?.effective_daily_budget);
  const hasBudget = Number.isFinite(dailyBudget);
  const hasUsedCalls = Number.isFinite(usedCalls);
  const nextAllowed = Number(entry?.next_allowed_in_seconds);
  let budgetLabel = "--";
  if (hasBudget) {
    const usedPart = hasUsedCalls ? usedCalls : 0;
    budgetLabel = `${usedPart}/${dailyBudget}`;
  }
  if (Number.isFinite(configuredDailyBudget) && Number.isFinite(effectiveDailyBudget) && configuredDailyBudget > effectiveDailyBudget) {
    budgetLabel = `${budgetLabel} (cfg ${configuredDailyBudget})`;
  }
  if (Number.isFinite(configuredDailyBudget) && Number.isFinite(effectiveDailyBudget) && effectiveDailyBudget > configuredDailyBudget) {
    budgetLabel = `${budgetLabel} (cfg ${configuredDailyBudget} ↑?)`;
  }
  if (Number.isFinite(nextAllowed) && nextAllowed > 0) {
    budgetLabel = `${budgetLabel} (next ${nextAllowed}s)`;
  }
  const inferredDowngrade = Number.isFinite(configuredDailyBudget) && Number.isFinite(effectiveDailyBudget) && effectiveDailyBudget < configuredDailyBudget;
  const inferredUpgrade = Number.isFinite(configuredDailyBudget) && Number.isFinite(effectiveDailyBudget) && effectiveDailyBudget > configuredDailyBudget;
  const isDowngrade = mismatchDirection === "downgrade" || (mismatchDirection !== "upgrade" && inferredDowngrade);
  const isUpgrade = mismatchDirection === "upgrade" || (mismatchDirection !== "downgrade" && inferredUpgrade);
  const budgetTooltip = isUpgrade
    ? `Effective limit (${effectiveDailyBudget}) exceeds configured plan (${configuredDailyBudget}). Provider may have been upgraded — consider updating the plan in Settings.`
    : isDowngrade
    ? `Quota signal detected. Effective limit capped at ${effectiveDailyBudget} (configured: ${configuredDailyBudget}). Update the plan in Settings if this persists.`
    : "";

  const budgetCell = (
    <Typography variant="caption" color={isUpgrade ? "warning.main" : isDowngrade ? "error.main" : "text.secondary"}>
      {budgetLabel}
    </Typography>
  );

  return (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Chip
            label={status}
            color={colorMap[status] ?? "default"}
            size="small"
            variant={status === "ok" ? "filled" : "outlined"}
          />
          {circuitOpen && <Chip label="circuit open" size="small" color="warning" variant="outlined" />}
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {entry?.tier ?? "--"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {planLabel}
        </Typography>
      </TableCell>
      <TableCell>
        {budgetTooltip ? (
          <Tooltip title={budgetTooltip} placement="top" arrow>
            <span>{budgetCell}</span>
          </Tooltip>
        ) : (
          budgetCell
        )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {entry?.last_checked ?? entry?.last_check ?? "--"}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function ReadinessTrendSparkline({ data }) {
  if (!Array.isArray(data) || data.length < 2) return null;
  const width = 320;
  const height = 72;
  const pad = 8;
  const scores = data.map((entry) => Number(entry?.score_pct ?? 0));
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = max - min || 1;
  const toX = (index) => pad + (index / (data.length - 1)) * (width - pad * 2);
  const toY = (value) => height - pad - ((value - min) / range) * (height - pad * 2);
  const points = data.map((entry, index) => `${toX(index)},${toY(Number(entry?.score_pct ?? 0))}`).join(" ");
  return (
    <Box data-testid="readiness-trend-sparkline" sx={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", display: "block" }}>
        <polyline points={points} fill="none" stroke="#1976d2" strokeWidth={2} strokeLinejoin="round" />
      </svg>
    </Box>
  );
}

const READINESS_BLOCKER_LABELS = {
  risk_within_limits: "Risk limits exceeded",
  providers_healthy: "Provider health check failed",
  allocation_balanced: "Allocation imbalance detected",
  anomaly_within_threshold: "Anomaly score above threshold",
  readiness_alert_policy_valid: "Readiness alert policy invalid",
  broker_sync_recent: "Broker sync is stale",
  autonomous_recency: "Autonomous cycle is stale",
  autonomous_cron_telemetry: "Autonomous scheduler telemetry missing",
};

function formatBlockerLabel(value) {
  if (!value) return "unknown";
  return READINESS_BLOCKER_LABELS[value] || value.replace(/_/g, " ");
}

function formatTimestamp(value) {
  if (!value) return "unknown";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

const AUTONOMOUS_NO_ACTION_LABELS = {
  candidate_scan_failed: "Candidate scan failed",
  no_open_positions_and_no_entry_candidates: "No tracked positions and no entry candidates",
  no_entry_candidates_after_filters: "No entry candidates after filters",
  open_positions_present_but_no_exit_signals: "No qualifying exit signals",
  idle_no_qualifying_actions: "No qualifying entry/exit actions",
};

function formatNoActionReason(value) {
  if (!value) return null;
  return AUTONOMOUS_NO_ACTION_LABELS[value] || String(value).replace(/_/g, " ");
}

function buildReadinessFixItems(readiness, analytics) {
  const blockers = Array.isArray(readiness?.blockers) ? readiness.blockers : [];
  const riskSummary = analytics?.risk_summary ?? {};
  const runtimeSummary = analytics?.runtime_summary ?? {};
  const items = [];

  blockers.forEach((blocker) => {
    if (blocker === "risk_within_limits") {
      const breaches = Array.isArray(riskSummary?.breaches) ? riskSummary.breaches : [];
      const details = [];
      const sectorName = riskSummary?.largest_sector_name || "largest sector";
      const sectorWeight = toNumberOrNull(riskSummary?.largest_sector_weight_pct);
      const sectorLimit = toNumberOrNull(riskSummary?.policy?.max_sector_weight_pct);
      const drawdown = toNumberOrNull(riskSummary?.max_drawdown_pct);
      const drawdownLimit = toNumberOrNull(riskSummary?.policy?.max_drawdown_pct);

      if (breaches.includes("sector_limit")) {
        details.push(
          sectorWeight !== null && sectorLimit !== null
            ? `Reduce ${sectorName} exposure below ${fmt(sectorLimit, 1)}% (current ${fmt(sectorWeight, 1)}%).`
            : "Reduce sector concentration until the portfolio is back inside policy."
        );
      }
      if (breaches.includes("drawdown_limit")) {
        details.push(
          drawdown !== null && drawdownLimit !== null
            ? `Review the drawdown breach: current ${fmt(drawdown, 1)}% vs limit ${fmt(drawdownLimit, 1)}%. Reset the paper portfolio baseline or de-risk the book before re-enabling automation.`
            : "Review the drawdown breach and bring the portfolio back within policy before re-enabling automation."
        );
      }
      if (details.length === 0) {
        details.push("Review the current risk summary and clear all breached limits before autonomous trading resumes.");
      }

      items.push({
        key: blocker,
        title: "Risk policy breach",
        detail: details.join(" "),
      });
      return;
    }

    if (blocker === "maintenance_recency") {
      const ageHours = toNumberOrNull(readiness?.metrics?.maintenance_age_hours);
      const maxAgeHours = toNumberOrNull(readiness?.policy?.max_maintenance_staleness_hours);
      const lastRunAt = runtimeSummary?.last_maintenance?.last_run_at;
      let detail = "Run runtime maintenance to refresh history housekeeping and clear the stale-maintenance gate.";
      if (lastRunAt || ageHours !== null || maxAgeHours !== null) {
        detail = `${detail} Last run ${formatTimestamp(lastRunAt)}${ageHours !== null ? ` (${fmt(ageHours, 1)}h ago)` : ""}${maxAgeHours !== null ? `; policy limit is ${fmt(maxAgeHours, 1)}h.` : "."}`;
      }

      items.push({
        key: blocker,
        title: "Maintenance is stale",
        detail,
      });
      return;
    }

    if (blocker === "provider_availability") {
      const providerOk = Number(readiness?.metrics?.provider_ok ?? 0);
      const providerTotal = Number(readiness?.metrics?.provider_total ?? 0);
      items.push({
        key: blocker,
        title: "No healthy provider available",
        detail: `Restore at least one healthy provider before autonomous trading resumes. Current healthy providers: ${providerOk}/${providerTotal}. Use the Provider Health section below to fix the failing endpoints.`,
      });
      return;
    }

    if (blocker === "autonomous_recency") {
      const runtime = readiness?.metrics?.autonomous_runtime ?? {};
      const ageMinutes = toNumberOrNull(runtime?.last_run_age_minutes);
      const thresholdMinutes = toNumberOrNull(runtime?.run_stale_threshold_minutes);
      items.push({
        key: blocker,
        title: "Autonomous run is stale",
        detail: `No recent autonomous cycle has completed${ageMinutes !== null ? ` (${fmt(ageMinutes, 1)}m since last run)` : ""}${thresholdMinutes !== null ? `; threshold ${fmt(thresholdMinutes, 0)}m.` : "."} Restore scheduler execution and verify /autonomous/run is being called on cadence.`,
      });
      return;
    }

    if (blocker === "autonomous_cron_telemetry") {
      const runtime = readiness?.metrics?.autonomous_runtime ?? {};
      const cronAvailable = Boolean(runtime?.cron_available);
      const cronAgeMinutes = toNumberOrNull(runtime?.cron_last_event_age_minutes);
      items.push({
        key: blocker,
        title: "Autonomous scheduler telemetry missing",
        detail: cronAvailable
          ? `Cron telemetry exists but is stale${cronAgeMinutes !== null ? ` (${fmt(cronAgeMinutes, 1)}m since last event)` : ""}. Verify cron job health and ensure scheduler events continue to post to /autonomous/cron-event.`
          : "Cron telemetry is unavailable. Configure AUTONOMOUS_CRON_LOG_PATH and ensure scheduler events are posting to /autonomous/cron-event.",
      });
      return;
    }

    items.push({
      key: blocker,
      title: formatBlockerLabel(blocker),
      detail: "Review the corresponding policy input and clear this blocker before autonomous trading resumes.",
    });
  });

  return items;
}

function buildProviderFixItems(entries) {
  return entries
    .filter(([, entry]) => entry?.status && entry.status !== "ok")
    .map(([name, entry]) => {
      const providerName = String(name || "provider");
      const errorType = String(entry?.error_type || "").toLowerCase();
      const message = String(entry?.message || "").toLowerCase();
      let detail = entry?.message
        ? `Investigate the latest ${providerName} response: ${entry.message}`
        : `Investigate the latest ${providerName} provider response and configuration.`;

      if (providerName.toLowerCase() === "binance" && (errorType === "http_451" || message.includes("451"))) {
        detail = "Binance is geo-blocked from this environment. Disable it for this deployment unless you have a compliant route from a supported region.";
      } else if (
        errorType === "http_429"
        || errorType === "quota_limit"
        || errorType === "quota_cooldown"
        || message.includes("quota")
        || message.includes("rate limit")
      ) {
        detail = `Rate limit or quota pressure detected for ${providerName}. Reduce refresh frequency, increase cooldown/backoff, or upgrade the plan before treating it as a primary provider.`;
      } else if (errorType === "http_403") {
        detail = `${providerName} is denying the current endpoint or plan tier. Verify entitlement access for this endpoint or demote it behind another primary provider.`;
      } else if (errorType === "http_401") {
        detail = `${providerName} credentials look invalid or mismatched. Re-check the API key, secret, and whether the app is pointed at the correct environment.`;
      } else if (errorType === "no_price") {
        detail = `${providerName} responded without quote data. Verify symbol/feed coverage and keep a fallback provider ahead of it for unsupported instruments.`;
      }

      return {
        key: providerName,
        title: `${providerName}: ${entry?.status ?? "issue"}`,
        detail,
      };
    });
}

const AUTO_REFRESH_SECONDS = 60;

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECONDS);
  const [marketSession, setMarketSession] = useState(null);
  const [lastSuccessfulRefreshAt, setLastSuccessfulRefreshAt] = useState(null);
  const [maintenanceRunLoading, setMaintenanceRunLoading] = useState(false);
  const [maintenanceRunMessage, setMaintenanceRunMessage] = useState("");
  const fetchRequestIdRef = useRef(0);
  const hasSummary = Boolean(summary);

  const fetchSummary = () => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;
    if (!hasSummary) {
      setLoading(true);
    }
    setError("");
    Promise.all([
      getDashboardSummary(),
      Promise.resolve().then(() => getMarketSession()).catch(() => null),
    ])
      .then(([summaryData, sessionData]) => {
        if (requestId !== fetchRequestIdRef.current) return;
        setSummary(summaryData);
        setMarketSession(sessionData);
        setError("");
        setLastSuccessfulRefreshAt(new Date());
      })
      .catch(() => {
        if (requestId !== fetchRequestIdRef.current) return;
        if (hasSummary) {
          // Keep existing dashboard data visible without a persistent blocking warning.
          setError("");
          return;
        }
        setError("Unable to load dashboard data. Please check your connection or try again.");
      })
      .finally(() => {
        if (requestId !== fetchRequestIdRef.current) return;
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSummary();
    setCountdown(AUTO_REFRESH_SECONDS);
    // eslint-disable-next-line
  }, [retryCount]);

  // Auto-refresh countdown
  useEffect(() => {
    if (process.env.NODE_ENV === "test") {
      return undefined;
    }
    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          setRetryCount((r) => r + 1);
          return AUTO_REFRESH_SECONDS;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  // Price lookup state
  const [ticker, setTicker] = useState("AAPL");
  const [priceResult, setPriceResult] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState("");
  const [fundamentalsResult, setFundamentalsResult] = useState(null);
  const [newsResult, setNewsResult] = useState([]);
  const [lookupWarning, setLookupWarning] = useState("");

  const handlePriceLookup = async (e) => {
    e.preventDefault();
    setPriceLoading(true);
    setPriceError("");
    setLookupWarning("");
    setPriceResult(null);
    setFundamentalsResult(null);
    setNewsResult([]);
    try {
      const normalizedTicker = ticker.trim();
      const fundamentalsRequest =
        typeof getProviderFundamentals === "function"
          ? getProviderFundamentals(normalizedTicker).catch(() => null)
          : Promise.resolve(null);
      const newsRequest =
        typeof getProviderNews === "function"
          ? getProviderNews(normalizedTicker).catch(() => null)
          : Promise.resolve(null);
      const [pricePayload, fundamentalsPayload, newsPayload] = await Promise.all([
        getProviderPrice(normalizedTicker),
        fundamentalsRequest,
        newsRequest,
      ]);
      window.__lastPriceLookupResult = pricePayload;
      setPriceResult(pricePayload);

      if (fundamentalsPayload?.fundamentals) {
        setFundamentalsResult(fundamentalsPayload);
      }
      if (Array.isArray(newsPayload?.news)) {
        setNewsResult(newsPayload.news.slice(0, 5));
      }

      if (!fundamentalsPayload?.fundamentals || !Array.isArray(newsPayload?.news) || newsPayload.news.length === 0) {
        setLookupWarning("Partial data: some fundamentals/news sources returned no data for this ticker.");
      }
    } catch (err) {
      setPriceError("Failed to fetch price. Try again.");
      window.__lastPriceLookupError = err;
    } finally {
      setPriceLoading(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const analytics = summary?.analytics ?? null;
  const readiness = summary?.readiness ?? null;
  const readinessLatest = summary?.readinessLatest ?? null;
  const readinessTrend = summary?.readinessTrend ?? null;
  const readinessActionLatest = summary?.readinessActionLatest ?? null;
  const alpaca = summary?.alpaca ?? null;
  const capital = summary?.capital ?? null;
  const health = summary?.health ?? null;
  const anomaly = summary?.anomaly ?? null;
  const screener = summary?.screener ?? null;
  const autonomous = summary?.autonomous ?? null;

  const plColor = analytics
    ? (toNumberOrNull(analytics.total_unrealized_pl) ?? 0) >= 0
      ? theme.palette.success.main
      : theme.palette.error.main
    : undefined;

  const totalMarketValue = toNumberOrNull(analytics?.total_market_value);
  const totalCostBasis = toNumberOrNull(analytics?.total_cost_basis);
  const totalUnrealizedPl = toNumberOrNull(analytics?.total_unrealized_pl);
  const totalReturnPct = toNumberOrNull(analytics?.total_return_pct);
  const maxDrawdownPct = toNumberOrNull(analytics?.history_summary?.max_drawdown_pct);
  const alpacaBuyingPower = toNumberOrNull(alpaca?.checks?.buying_power);
  const alpacaCash = toNumberOrNull(alpaca?.checks?.cash);
  const reinvestmentCapitalCurrent = toNumberOrNull(capital?.current_available_capital);
  const reinvestmentCapitalPeak = toNumberOrNull(capital?.peak_available_capital);
  const reinvestmentCapitalRatio = (
    reinvestmentCapitalCurrent !== null
    && reinvestmentCapitalPeak !== null
    && reinvestmentCapitalPeak > 0
  )
    ? (reinvestmentCapitalCurrent / reinvestmentCapitalPeak) * 100
    : null;
  const screenerTopSymbol = Array.isArray(screener?.latest)
    ? screener.latest[0]?.symbol
    : screener?.latest?.symbol;

  const readinessScore = readiness?.score_pct ?? null;
  const readinessReady = readiness?.ready ?? null;
  const readinessBlockers = readiness?.blockers ?? [];
  const readinessMetrics = readiness?.metrics ?? {};
  const readinessRiskGateMode = readinessMetrics?.risk_gate_mode || null;
  const sectorRebalanceRemediation = readinessMetrics?.sector_rebalance_remediation ?? null;
  const diversificationRemediation = readinessMetrics?.diversification_candidate_remediation ?? null;
  const concentrationSolutionAvailable = Boolean(
    readinessMetrics?.concentration_solution_available
      || sectorRebalanceRemediation?.available
      || diversificationRemediation?.available
  );
  const concentrationSolutionCandidates = Array.isArray(diversificationRemediation?.candidates)
    ? diversificationRemediation.candidates
    : [];
  const hasMaintenanceBlocker = readinessBlockers.includes("maintenance_recency");
  const readinessBlockerLabels = readinessBlockers.map(formatBlockerLabel);
  const readinessAsOf = readiness?.as_of || readinessLatest?.as_of || "--";
  const readinessChecksPassed = readiness?.checks
    ? Object.values(readiness.checks).filter(Boolean).length
    : 0;
  const readinessChecksTotal = readiness?.checks ? Object.keys(readiness.checks).length : 0;
  const blockerTransitions = Array.isArray(readinessTrend?.blocker_transitions)
    ? readinessTrend.blocker_transitions.slice(-5).reverse()
    : [];
  const readinessFixItems = buildReadinessFixItems(readiness, analytics);

  const healthEntries = health && typeof health === "object"
    ? Object.entries(health).filter(([, v]) => v && typeof v === "object")
    : [];
  const activeHealthEntries = healthEntries.filter(([, v]) => v?.active !== false);
  const displayedHealthEntries = activeHealthEntries.length > 0 ? activeHealthEntries : healthEntries;
  const supplementalHealthEntries = activeHealthEntries.length > 0
    ? healthEntries.filter(([, entry]) => entry?.active === false)
    : [];

  const providerCounts = readiness?.metrics?.provider_counts || null;
  const providerPrimaryCountFromApi = Number(providerCounts?.primary);
  const providerSupplementalCountFromApi = Number(providerCounts?.supplemental);
  const providerTotalCountFromApi = Number(readiness?.metrics?.provider_total);
  const providerOkFromApi = Number(readiness?.metrics?.provider_ok);

  const providerEntriesForKpi = activeHealthEntries.length > 0 ? activeHealthEntries : healthEntries;
  const providersOk = providerEntriesForKpi.filter(([, v]) => v?.status === "ok").length;
  const providersOkDisplay = Number.isFinite(providerOkFromApi) ? providerOkFromApi : providersOk;
  const providersTotalDisplay = Number.isFinite(providerTotalCountFromApi)
    ? providerTotalCountFromApi
    : (providerEntriesForKpi.length + supplementalHealthEntries.length);
  const primaryProviderCountDisplay = Number.isFinite(providerPrimaryCountFromApi)
    ? providerPrimaryCountFromApi
    : providerEntriesForKpi.length;
  const supplementalProviderCountDisplay = Number.isFinite(providerSupplementalCountFromApi)
    ? providerSupplementalCountFromApi
    : supplementalHealthEntries.length;
  const quotaLimitedProviders = providerEntriesForKpi.filter(([, entry]) => {
    if (!entry || entry.status !== "warn") return false;
    const errorType = String(entry.error_type || "").toLowerCase();
    const message = String(entry.message || "").toLowerCase();
    return (
      errorType === "quota_limit"
      || errorType === "quota_cooldown"
      || errorType === "http_429"
      || (errorType === "http_403" && message.includes("free-tier"))
      || message.includes("quota")
      || message.includes("rate limit")
    );
  });
  const circuitOpenProviders = providerEntriesForKpi.filter(([, entry]) => {
    const errorType = String(entry?.error_type || "").toLowerCase();
    const message = String(entry?.message || "").toLowerCase();
    return errorType === "circuit_open" || message.includes("circuit open");
  });
  const providerFixItems = buildProviderFixItems(displayedHealthEntries);
  const screenerFallbackCount = Number.isFinite(Number(screener?.fallback_count))
    ? Number(screener.fallback_count)
    : 0;
  const screenerLastBackendUsed = screener?.last_backend_used || "primary";
  const screenerLastFallbackAt = screener?.last_fallback_at || null;
  const screenerLastPrimarySuccessAt = screener?.last_primary_success_at || null;
  const screenerLastError = screener?.last_error || null;
  const screenerParityGateStatus = screener?.parity_gate_status || null;
  const screenerParityEligible = Boolean(screener?.parity_promotion_eligible);
  const screenerParityRuns = Number.isFinite(Number(screener?.parity_total_shadow_runs))
    ? Number(screener.parity_total_shadow_runs)
    : 0;
  const screenerParityDetails = screener?.parity_details || null;
  const autonomousLatestRun = autonomous?.latest_run ?? null;
  const autonomousRunsLive = toNumberOrNull(autonomous?.runs_live);
  const autonomousRunsTotal = toNumberOrNull(autonomous?.runs_total);
  const autonomousLastRunAt = autonomous?.last_run_at || autonomousLatestRun?.ran_at || null;
  const autonomousLastRunError = autonomous?.last_run_error || autonomousLatestRun?.run_error || null;
  const autonomousLastRunAgeMinutes = toNumberOrNull(autonomous?.last_run_age_minutes);
  const autonomousStaleThresholdMinutes = toNumberOrNull(autonomous?.stale_threshold_minutes);
  const autonomousRunStale = Boolean(autonomous?.run_stale);
  const autonomousNoActionReasonRaw = autonomous?.last_run_no_action_reason || autonomousLatestRun?.no_action_reason || null;
  const autonomousNoActionReason = formatNoActionReason(autonomousNoActionReasonRaw);
  const autonomousNoActionDetails = autonomous?.last_run_no_action_details || autonomousLatestRun?.no_action_details || null;
  const autonomousStaleSummary = autonomousRunStale
    ? `Stale: no completed cycle for ${fmt(autonomousLastRunAgeMinutes ?? 0, 1)} minutes`
    : null;
  const autonomousFreshnessSummary = autonomousRunStale
    ? autonomousStaleSummary
    : autonomousLastRunAgeMinutes !== null
      ? `Last cycle ${fmt(autonomousLastRunAgeMinutes, 1)} min ago`
      : "No completed cycle";
  const autonomousLatestEntries = Array.isArray(autonomousLatestRun?.positions_entered_count)
    ? autonomousLatestRun.positions_entered_count
    : toNumberOrNull(autonomousLatestRun?.positions_entered_count);
  const autonomousLatestExits = Array.isArray(autonomousLatestRun?.exits_executed_count)
    ? autonomousLatestRun.exits_executed_count
    : toNumberOrNull(autonomousLatestRun?.exits_executed_count);
  const autonomousCapital = autonomousLatestRun?.candidate_resolution?.capital ?? null;
  const autonomousDeployableCapital = toNumberOrNull(autonomousCapital?.deployable_capital);
  const autonomousSourceCapital = toNumberOrNull(autonomousCapital?.available_capital);
  const autonomousCapitalBufferPct = toNumberOrNull(autonomousCapital?.capital_safety_buffer_pct);
  const autonomousCapitalBufferUsd = toNumberOrNull(autonomousCapital?.capital_safety_buffer_usd);
  const autonomousCapitalModeRaw = autonomousCapital?.source_mode;
  const autonomousCapitalModeLabel = autonomousCapitalModeRaw
    ? String(autonomousCapitalModeRaw).replace(/_/g, " ")
    : null;

  const handleRunMaintenance = async () => {
    setMaintenanceRunLoading(true);
    setMaintenanceRunMessage("");
    try {
      await runRuntimeMaintenance(false);
      setMaintenanceRunMessage("Runtime maintenance completed. Refreshing readiness data...");
      setRetryCount((c) => c + 1);
    } catch {
      setMaintenanceRunMessage("Runtime maintenance failed. Check server logs and retry.");
    } finally {
      setMaintenanceRunLoading(false);
    }
  };

  return (
    <ResponsiveContainer>
      <Card elevation={1} sx={{ width: "100%" }}>
        <CardContent>
          {/* Header */}
          <Stack direction="column" mb={2} spacing={1}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: 22, sm: 28 } }}>
                Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: 14, sm: 16 } }}>
                Portfolio, readiness, and system health at a glance.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              {readiness && (
                <Chip
                  label={`Readiness: ${readinessReady ? "READY" : "BLOCKED"}`}
                  size="small"
                  color={readinessReady ? "success" : "warning"}
                  variant={readinessReady ? "filled" : "outlined"}
                />
              )}
              {readiness && (
                <Chip
                  label={`Score: ${fmt(readinessScore ?? 0, 1)}%`}
                  size="small"
                  color={readinessReady ? "success" : "warning"}
                  variant="outlined"
                />
              )}
              <Chip
                label={`Auto-refresh: ${countdown}s`}
                size="small"
                variant="outlined"
                color={countdown <= 10 ? "warning" : "default"}
                sx={{
                  minWidth: 132,
                  "& .MuiChip-label": {
                    fontVariantNumeric: "tabular-nums",
                    fontFeatureSettings: '"tnum"',
                  },
                }}
              />
              {lastSuccessfulRefreshAt && (
                <Chip
                  label={`Last success: ${lastSuccessfulRefreshAt.toLocaleTimeString()}`}
                  size="small"
                  variant="outlined"
                  color="default"
                  sx={{
                    "& .MuiChip-label": {
                      fontVariantNumeric: "tabular-nums",
                      fontFeatureSettings: '"tnum"',
                    },
                  }}
                />
              )}
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setRetryCount((c) => c + 1)}
                disabled={loading}
                sx={{ minHeight: 32, px: 1.5 }}
              >
                Refresh
              </Button>
              {hasMaintenanceBlocker && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRunMaintenance}
                  disabled={maintenanceRunLoading}
                  sx={{ minHeight: 32, px: 1.5 }}
                >
                  {maintenanceRunLoading ? "Running maintenance..." : "Run maintenance"}
                </Button>
              )}
            </Stack>
          </Stack>

          {error && (
            <Alert
              severity={hasSummary ? "warning" : "error"}
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => setRetryCount((c) => c + 1)}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {loading && (
            <Stack spacing={2} mt={2}>
              <Skeleton variant="rectangular" height={80} />
              <Skeleton variant="rectangular" height={60} />
              <Skeleton variant="rectangular" height={120} />
            </Stack>
          )}

          {!loading && (
            <>
              {/* Market Session Banner */}
              {marketSession && (
                <Stack direction="row" spacing={1} alignItems="center" mb={2} sx={{ flexWrap: "wrap" }}>
                  <Chip
                    label={`Market: ${marketSession.session?.replace("_", " ").toUpperCase() ?? "--"}`}
                    color={
                      marketSession.session === "open"
                        ? "success"
                        : marketSession.session === "pre_market" || marketSession.session === "after_hours"
                        ? "warning"
                        : "default"
                    }
                    variant={marketSession.market_open ? "filled" : "outlined"}
                    size="small"
                  />
                  {marketSession.extended_open && (
                    <Chip label="Extended Hours" color="info" size="small" variant="outlined" />
                  )}
                  {!marketSession.market_open && marketSession.next_open_text && (
                    <Typography variant="caption" color="text.secondary">
                      {String(marketSession.next_open_text).startsWith("Next open:")
                        ? marketSession.next_open_text
                        : `Next open: ${marketSession.next_open_text}`}
                    </Typography>
                  )}
                  {marketSession.as_of_et && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                      ET: {marketSession.as_of_et}
                    </Typography>
                  )}
                </Stack>
              )}
              {/* External Screener Mode Banner */}
              {screener?.mode === "external" && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="external-screener-banner">
                  <strong>External Screener Mode</strong> — WG is delegating screener calls to TradePilot
                  Screener ({screener.base_url ?? "http://localhost:8060/screener"}). Screener results will be
                  empty until the standalone service is fully implemented.
                  <Typography variant="caption" component="div" sx={{ mt: 0.75 }}>
                    Runtime status: last served by {screenerLastBackendUsed === "fallback" ? "embedded fallback" : "external primary"}; fallback events: {screenerFallbackCount}.
                    {screenerLastPrimarySuccessAt ? ` Last external success: ${screenerLastPrimarySuccessAt}.` : ""}
                    {screenerLastFallbackAt ? ` Last fallback: ${screenerLastFallbackAt}.` : ""}
                    {screenerLastError ? ` Last fallback error: ${screenerLastError}.` : ""}
                    {screenerParityGateStatus ? ` Parity gate: ${screenerParityGateStatus}.` : ""}
                    {screenerParityGateStatus ? ` Promotion eligible: ${screenerParityEligible ? "yes" : "no"}.` : ""}
                    {screenerParityGateStatus ? ` Shadow runs: ${screenerParityRuns}.` : ""}
                    {screenerParityDetails ? ` ${screenerParityDetails}` : ""}
                  </Typography>
                </Alert>
              )}

              {/* Portfolio KPIs */}
              {analytics && (
                <>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Portfolio Overview
                  </Typography>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={2}
                    sx={{ flexWrap: "wrap" }}
                    mb={2}
                  >
                    <KPICard
                      label="Market Value"
                      value={totalMarketValue === null ? "$--" : `$${fmt(totalMarketValue)}`}
                      sub={totalCostBasis === null ? "Cost basis: $--" : `Cost basis: $${fmt(totalCostBasis)}`}
                    />
                    <KPICard
                      label="Available Capital"
                      value={alpacaBuyingPower === null ? "$--" : `$${fmt(alpacaBuyingPower)}`}
                      sub={
                        alpacaCash === null
                          ? "Alpaca buying power"
                          : `Alpaca buying power · Cash: $${fmt(alpacaCash)}`
                      }
                      highlight={
                        alpacaBuyingPower !== null && alpacaBuyingPower > 0
                          ? theme.palette.info.main
                          : undefined
                      }
                    />
                    <KPICard
                      label="Unrealized P/L"
                      value={
                        totalUnrealizedPl === null
                          ? "$--"
                          : `${totalUnrealizedPl >= 0 ? "+" : ""}$${fmt(totalUnrealizedPl)}`
                      }
                      sub={
                        totalReturnPct === null
                          ? "-- total return"
                          : `${totalReturnPct >= 0 ? "+" : ""}${fmt(totalReturnPct)}% total return`
                      }
                      highlight={plColor}
                    />
                    <KPICard
                      label="Max Drawdown"
                      value={maxDrawdownPct === null ? "--" : `${fmt(maxDrawdownPct)}%`}
                      sub="Peak-to-trough"
                    />
                    <KPICard
                      label="Positions"
                      value={analytics.positions?.length ?? 0}
                      sub={`${analytics.sleeve_breakdown?.length ?? 0} sleeve(s)`}
                    />
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Autonomous Activity */}
              {autonomous && autonomous.status !== "error" && (
                <>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Autonomous Activity
                  </Typography>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={2}
                    sx={{ flexWrap: "wrap" }}
                    mb={1.5}
                  >
                    <KPICard
                      label="Engine"
                      value={autonomous.armed ? "Armed" : "Disarmed"}
                      sub={
                        autonomous?.cron_status?.available
                          ? `Cron telemetry active | ${autonomousFreshnessSummary}`
                          : `Cron telemetry unavailable | ${autonomousFreshnessSummary}`
                      }
                      highlight={autonomous.armed ? theme.palette.success.main : theme.palette.warning.main}
                    />
                    <KPICard
                      label="Live Runs"
                      value={autonomousRunsLive === null ? "--" : `${fmt(autonomousRunsLive, 0)}`}
                      sub={autonomousRunsTotal === null ? "Total runs: --" : `Total runs: ${fmt(autonomousRunsTotal, 0)}`}
                    />
                    <KPICard
                      label="Latest Cycle"
                      value={autonomousLatestRun?.run_id ? autonomousLatestRun.run_id.slice(0, 8) : "--"}
                      sub={autonomousLastRunAt ? formatTimestamp(autonomousLastRunAt) : "No completed cycle"}
                    />
                    <KPICard
                      label="Last Actions"
                      value={`E:${autonomousLatestEntries === null ? "--" : fmt(autonomousLatestEntries, 0)} / X:${autonomousLatestExits === null ? "--" : fmt(autonomousLatestExits, 0)}`}
                      sub={autonomousLastRunError ? `Error: ${autonomousLastRunError}` : autonomousNoActionReason ? `Idle reason: ${autonomousNoActionReason}` : "No run error"}
                      highlight={autonomousLastRunError ? theme.palette.error.main : undefined}
                    />
                    <KPICard
                      label="Deployable Capital"
                      value={autonomousDeployableCapital === null ? "$--" : `$${fmt(autonomousDeployableCapital)}`}
                      sub={
                        autonomousSourceCapital === null
                          ? "Awaiting capital telemetry"
                          : `Source $${fmt(autonomousSourceCapital)}${autonomousCapitalModeLabel ? ` | Mode: ${autonomousCapitalModeLabel}` : ""}${autonomousCapitalBufferPct !== null ? ` | Buffer: ${fmt(autonomousCapitalBufferPct)}%` : ""}${autonomousCapitalBufferUsd !== null ? ` ($${fmt(autonomousCapitalBufferUsd)})` : ""}`
                      }
                    />
                  </Stack>
                  {autonomousRunStale && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      {autonomousStaleSummary}
                      {autonomousStaleThresholdMinutes !== null ? ` (threshold ${fmt(autonomousStaleThresholdMinutes, 0)}m).` : "."}
                    </Alert>
                  )}
                  {autonomousLastRunError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Last autonomous cycle reported an error: {String(autonomousLastRunError)}
                    </Alert>
                  )}
                  {!autonomousLastRunError && autonomousNoActionReason && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Last cycle completed with no executed actions: {autonomousNoActionReason}.
                      {autonomousNoActionDetails?.accepted_candidates_max != null
                        ? ` Candidate max after filters: ${fmt(autonomousNoActionDetails.accepted_candidates_max, 0)}.`
                        : ""}
                      {autonomousNoActionDetails?.open_positions_before_count != null
                        ? ` Open tracked positions: ${fmt(autonomousNoActionDetails.open_positions_before_count, 0)}.`
                        : ""}
                    </Alert>
                  )}
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Readiness Gate */}
              {readiness && (
                <>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Autonomous Readiness
                  </Typography>
                  {maintenanceRunMessage && (
                    <Alert
                      severity={maintenanceRunMessage.includes("failed") ? "warning" : "success"}
                      sx={{ mb: 1 }}
                    >
                      {maintenanceRunMessage}
                    </Alert>
                  )}
                  <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={1} sx={{ flexWrap: "wrap" }}>
                    <KPICard
                      label="Readiness Score"
                      value={`${fmt(readinessScore, 1)}%`}
                      sub={readinessReady ? "System ready for unattended run" : "Blockers detected - see below"}
                      highlight={readinessReady ? theme.palette.success.main : theme.palette.warning.main}
                    />
                    <KPICard
                      label="Checks Passed"
                      value={`${readinessChecksPassed} / ${readinessChecksTotal}`}
                      sub={`As of ${readinessAsOf}`}
                    />
                    <KPICard
                      label="Providers OK"
                      value={`${providersOkDisplay} / ${providersTotalDisplay}`}
                      sub={activeHealthEntries.length > 0 ? `Primary: ${primaryProviderCountDisplay}, Supplemental: ${supplementalProviderCountDisplay}` : "Data providers responding"}
                    />
                  </Stack>
                  <Alert severity={readinessReady ? "success" : "warning"} sx={{ mb: 1 }}>
                    {readinessReady
                      ? "All readiness checks passed. System is ready."
                      : `Readiness blockers: ${readinessBlockerLabels.join(", ") || "unknown"}`}
                  </Alert>
                  {!readinessReady && readinessFixItems.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Clear the blockers
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {readinessFixItems.map((item) => (
                          <Box key={item.key}>
                            <Typography variant="caption" fontWeight={700} display="block">
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.detail}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  )}
                  {(concentrationSolutionAvailable || readinessRiskGateMode === "concentration_solution_mode") && (
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Concentration Solutions
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Sector imbalance is being treated as a solvable condition. Use these actions to rebalance rather than pausing autonomous execution.
                      </Typography>

                        <Box
                          sx={{
                            mb: 1,
                            p: 1,
                            borderRadius: 1,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            bgcolor: alpha(theme.palette.success.main, 0.05),
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} display="block">
                            Alpaca buying power
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {alpacaBuyingPower != null
                              ? `Live broker account: $${fmt(alpacaBuyingPower)}${alpacaCash != null ? ` · Cash: $${fmt(alpacaCash)}` : ""}`
                              : "Live broker account: unavailable"}
                          </Typography>
                        </Box>

                      <Box
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          bgcolor: alpha(theme.palette.info.main, 0.06),
                        }}
                      >
                        <Typography variant="caption" fontWeight={700} display="block">
                          Reinvestment pool available
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {capital?.current_available_capital != null
                            ? `Current pool: $${fmt(capital.current_available_capital)}${capital?.peak_available_capital != null ? ` · Peak: $${fmt(capital.peak_available_capital)}` : ""}`
                            : "Current pool: --"}
                          {capital?.current_available_capital != null && capital?.peak_available_capital > 0
                            ? ` (${fmt((capital.current_available_capital / capital.peak_available_capital) * 100, 1)}% of peak)`
                            : ""}
                        </Typography>
                      </Box>

                      <Stack spacing={1.25}>
                        <Box>
                          <Typography variant="caption" fontWeight={700} display="block">
                            Overweight sector exit
                          </Typography>
                          {sectorRebalanceRemediation?.available ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Candidate: {sectorRebalanceRemediation?.symbol || "--"}
                              {sectorRebalanceRemediation?.sector ? ` (${sectorRebalanceRemediation.sector})` : ""}
                              {Number.isFinite(Number(sectorRebalanceRemediation?.sector_weight_pct))
                                ? ` at ${fmt(Number(sectorRebalanceRemediation.sector_weight_pct), 1)}% sector weight`
                                : ""}
                              {Number.isFinite(Number(sectorRebalanceRemediation?.exit_priority_score))
                                ? `, priority ${fmt(Number(sectorRebalanceRemediation.exit_priority_score), 3)}`
                                : ""}
                              .
                              {sectorRebalanceRemediation?.rationale ? ` ${sectorRebalanceRemediation.rationale}` : ""}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary" display="block">
                              No sell remediation candidate available right now.
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="caption" fontWeight={700} display="block">
                            Diversification acquisition candidates
                          </Typography>
                          {diversificationRemediation?.available && concentrationSolutionCandidates.length > 0 ? (
                            <Stack spacing={0.5} sx={{ mt: 0.25 }}>
                              {concentrationSolutionCandidates.map((candidate) => (
                                <Typography
                                  key={`${candidate?.symbol || "UNK"}-${candidate?.sector || "Unknown"}`}
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {candidate?.symbol || "--"}
                                  {candidate?.sector ? ` (${candidate.sector})` : ""}
                                  {Number.isFinite(Number(candidate?.composite_score))
                                    ? ` score ${fmt(Number(candidate.composite_score), 2)}`
                                    : ""}
                                  {candidate?.reason ? ` - ${candidate.reason}` : ""}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary" display="block">
                              No outside-sector candidates currently meet diversification filters.
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  )}
                  {Array.isArray(readinessTrend?.score_series) && readinessTrend.score_series.length > 1 && (
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Readiness Trend
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Trend direction: {readinessTrend?.trend_direction || "flat"}
                      </Typography>
                      <ReadinessTrendSparkline data={readinessTrend.score_series} />
                    </Paper>
                  )}
                  {blockerTransitions.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Blocker Timeline
                      </Typography>
                      {blockerTransitions.map((transition, index) => (
                        <Typography key={`${transition.at || "na"}-${index}`} variant="caption" color="text.secondary" display="block">
                          {transition.at || "n/a"}: {transition.from?.map(formatBlockerLabel).join(", ") || "clear"} to {transition.to?.map(formatBlockerLabel).join(", ") || "clear"}
                        </Typography>
                      ))}
                    </Paper>
                  )}
                  {readinessActionLatest && (
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Last Readiness Action
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Type: {readinessActionLatest.event_type || "none"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Status: {readinessActionLatest.status || "unknown"}
                        {readinessActionLatest.dry_run ? " (dry-run)" : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Reason: {readinessActionLatest.reason || "n/a"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        As of: {readinessActionLatest.as_of || "n/a"}
                      </Typography>
                    </Paper>
                  )}
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Aggregate Highlights */}
              {(anomaly || screener) && (
                <>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Aggregate Highlights
                  </Typography>
                  <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={2} sx={{ flexWrap: "wrap" }}>
                    {anomaly && (
                      <KPICard
                        label="Anomaly Score"
                        value={`${fmt((Number(anomaly?.score ?? 0) * 100), 1)}%`}
                        sub={`Mode: ${anomaly?.mode ?? "unknown"}`}
                      />
                    )}
                    {screener && (
                      <KPICard
                        label="Screener Results"
                        value={screener?.count ?? 0}
                        sub={`Top symbol: ${screenerTopSymbol ?? "none"}`}
                      />
                    )}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Provider Health */}
              {healthEntries.length > 0 && (
                <>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                    <Typography variant="h6" fontWeight={600}>
                      Provider Health
                    </Typography>
                    {quotaLimitedProviders.length > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`Quota-limited: ${quotaLimitedProviders.length}`}
                      />
                    )}
                    {circuitOpenProviders.length > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`Circuit-open: ${circuitOpenProviders.length}`}
                      />
                    )}
                  </Stack>
                  <Box sx={{ width: "100%", overflowX: "auto", mb: 2 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: "action.hover" }}>
                            <TableCell>Provider</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Tier</TableCell>
                            <TableCell>Plan</TableCell>
                            <TableCell>Budget</TableCell>
                            <TableCell>
                              <Tooltip title="Last health check timestamp" arrow>
                                <span>Last Check</span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayedHealthEntries.map(([name, entry]) => (
                            <ProviderHealthRow key={name} name={name} entry={entry} />
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  {supplementalProviderCountDisplay > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
                      Supplemental providers hidden from primary view: {supplementalProviderCountDisplay}. These are available for diagnostics but do not count toward primary provider health KPIs.
                    </Typography>
                  )}
                  {providerFixItems.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                        Provider remediation
                      </Typography>
                      <Stack spacing={0.75}>
                        {providerFixItems.map((item) => (
                          <Box key={item.key}>
                            <Typography variant="caption" fontWeight={700} display="block">
                              {item.title}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {item.detail}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Alert>
                  )}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Provider tip: HTTP 401 usually means invalid, expired, or wrong-environment keys (for example paper keys against live endpoints).
                    <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                      Free-tier note: warning status can indicate quota/plan limits (for example 403/429 or provider-specific quota notes), not necessarily a bad key.
                    </Typography>
                  </Alert>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Price Lookup */}
              <Typography variant="h6" fontWeight={600} mb={1}>
                Price Lookup
              </Typography>
              <Box mb={3}>
                <Card variant="outlined" sx={{ maxWidth: { xs: "100%", sm: 420 }, p: { xs: 1.5, sm: 2 }, width: "100%" }}>
                  <form
                    onSubmit={handlePriceLookup}
                    style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 8 }}
                  >
                    <TextField
                      label="Ticker"
                      value={ticker}
                      onChange={(e) => setTicker(e.target.value)}
                      placeholder="e.g. AAPL"
                      size="small"
                      fullWidth
                      required
                      disabled={priceLoading}
                      sx={{ mb: isMobile ? 1 : 0 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={priceLoading || !ticker.trim()}
                      sx={{ minHeight: 40, width: isMobile ? "100%" : "auto" }}
                    >
                      {priceLoading ? <CircularProgress size={20} /> : "Get Price"}
                    </Button>
                  </form>
                  {priceError && <Alert severity="error" sx={{ mt: 2 }}>{priceError}</Alert>}
                  {priceResult && (
                    <Box mt={2}>
                      <Typography sx={{ fontSize: { xs: 15, sm: 16 } }} data-testid="price-lookup-result">
                        <strong>{priceResult.ticker}:</strong>{" "}
                        {priceResult.price !== null ? `$${priceResult.price}` : "Not available"}
                        {priceResult.error && (
                          <span style={{ color: "#888", marginLeft: 8 }}>({priceResult.error})</span>
                        )}
                      </Typography>

                      {lookupWarning && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          {lookupWarning}
                        </Alert>
                      )}

                      {fundamentalsResult?.fundamentals && (
                        <Paper variant="outlined" sx={{ p: 1.25, mt: 1.25 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Fundamentals ({fundamentalsResult?.provider || "unknown"})
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Company: {fundamentalsResult.fundamentals.company_name || "--"}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Sector: {fundamentalsResult.fundamentals.sector || "--"}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Industry: {fundamentalsResult.fundamentals.industry || "--"}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Market Cap: {Number.isFinite(Number(fundamentalsResult.fundamentals.market_cap)) ? `$${fmt(fundamentalsResult.fundamentals.market_cap, 0)}` : "--"}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            P/E: {Number.isFinite(Number(fundamentalsResult.fundamentals.pe_ratio)) ? fmt(fundamentalsResult.fundamentals.pe_ratio, 2) : "--"}
                          </Typography>
                        </Paper>
                      )}

                      {newsResult.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 1.25, mt: 1.25 }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                            Latest News
                          </Typography>
                          {newsResult.map((row, idx) => (
                            <Typography key={`${row.headline || "news"}-${idx}`} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                              {row.headline}
                            </Typography>
                          ))}
                        </Paper>
                      )}
                    </Box>
                  )}
                </Card>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </ResponsiveContainer>
  );
};

export default Dashboard;
